import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {EstadoOnboardingPainel, OnboardingPainelService} from '../onboarding-painel.service';
import {OnbFocoService} from '../apoio/onb-foco.service';
import {ConstantsService} from '../../fidelidade/ConstantsService';
import {FotoService} from '../../services/foto.service';
import {BannerService} from '../../services/banner.service';

/**
 * Passo 2 · Como sua loja funciona. Capa, logo e banners (opcionais), entrega e/ou retirada, endereço (CEP completa o resto), taxa
 * única, tempos e pedido mínimo. Taxa por bairro ou distância fica para depois, em Formas de entrega: aqui é para sair vendendo.
 */
@Component({
  selector: 'app-passo-loja',
  templateUrl: './passo-loja.component.html',
  styleUrls: ['./passo-loja.component.scss']
})
export class PassoLojaComponent implements OnInit, OnDestroy {
  @Input() estado: EstadoOnboardingPainel;
  @Output() concluido = new EventEmitter<EstadoOnboardingPainel>();

  delivery = true;
  retirada = true;
  /** toggles vieram do que ele contou para a Mia na conversa */
  operacaoDaMia = false;
  enderecoSalvo = '';
  manterEndereco = false;
  cep = '';
  rua = '';
  numero = '';
  complemento = '';
  bairro = '';
  cidade = '';
  uf = '';
  buscandoCep = false;
  avisoCep = '';
  taxa = '';
  tempoMinimo = '40';
  tempoMaximo = '60';
  pedidoMinimo = '';
  tempoRetiradaMinimo = '15';
  tempoRetiradaMaximo = '25';
  salvando = false;
  erro = '';

  /** empresa da sessão (id, nome, logo, capa); o upload trabalha num objeto à parte para abrir a área de envio mesmo com imagem */
  empresa: any = {};
  enviando: 'logo' | 'capa' | null = null;
  imagemNova: {logo: string | null; capa: string | null} = {logo: null, capa: null};
  erroImagem = '';
  /** banners do carrossel do cardápio: opcionais; aqui só nome e imagem (o que o banner faz ao tocar fica em Banners) */
  banners: any[] = [];
  bannerNovo: {nome: string; linkImagem: string | null} | null = null;
  salvandoBanner = false;
  erroBanner = '';
  private inscricao: any;

  constructor(private onboarding: OnboardingPainelService, private foco: OnbFocoService,
              private constants: ConstantsService, private fotos: FotoService, private bannersService: BannerService) {}

  ngOnInit(): void {
    this.inscricao = this.constants.empresa$.subscribe((empresa: any) => this.empresa = empresa || {});
    this.carregueLoja();
    this.carregueBanners();
  }

  ngOnDestroy(): void {
    if (this.inscricao) this.inscricao.unsubscribe();
  }

  get iniciais(): string {
    return String(this.empresa?.nome || '').split(' ').filter(p => p).slice(0, 2).map(p => p[0].toUpperCase()).join('') || '?';
  }

  abraEnvio(qual: 'logo' | 'capa'): void {
    this.bannerNovo = null;
    this.imagemNova = {logo: null, capa: null};
    this.erroImagem = '';
    this.enviando = qual;
  }

  /** Mesmo caminho da tela Fotos da loja: o upload grava o arquivo, aqui só aponta a empresa para ele. */
  salveImagem(qual: 'logo' | 'capa', retorno: any): void {
    const arquivo = this.imagemNova[qual];
    this.erroImagem = '';
    const salvar = qual === 'logo' ? this.fotos.salveLogoEmpresa({id: this.empresa.id, logo: arquivo})
      : this.fotos.salveCapaEmpresa({id: this.empresa.id, capa: arquivo});
    salvar.then(() => {
      retorno.salvou();
      this.empresa[qual] = arquivo;
      this.enviando = null;
    }).catch((e: any) => {
      retorno.erro(e);
      this.erroImagem = typeof e === 'string' ? e : `Não foi possível salvar ${qual === 'logo' ? 'o logo' : 'a capa'}. Tente de novo.`;
    });
  }

  private carregueBanners(): void {
    this.bannersService.obtenhaBanners(this.empresa).then((lista: any) => this.banners = lista || []).catch(() => {});
  }

  abraNovoBanner(): void {
    this.enviando = null;
    this.erroBanner = '';
    this.bannerNovo = {nome: '', linkImagem: null};
    setTimeout(() => document.getElementById('onb-banner-nome')?.focus(), 0);
  }

  /** O upload já gravou o arquivo; o banner só nasce no Salvar banner. */
  imagemDoBannerEnviada(retorno: any): void { retorno.salvou(); }

  salveBanner(): void {
    if (this.salvandoBanner || !this.bannerNovo) return;
    const nome = this.bannerNovo.nome.trim();
    if (!this.bannerNovo.linkImagem) return this.foco.mostre('onb-banner-imagem', 'Escolha a imagem do banner. Imagem larga, deitada.');
    if (!nome) return this.foco.mostre('onb-banner-nome', 'Dê um nome para o banner. Ex.: Combo da semana.');
    this.salvandoBanner = true;
    this.erroBanner = '';
    this.bannersService.salveBanner({nome, linkImagem: this.bannerNovo.linkImagem, comando: 'NADA'}).then(() => {
      this.bannerNovo = null;
      this.carregueBanners();
    }).catch((e: any) => this.erroBanner = typeof e === 'string' ? e : 'Não foi possível salvar o banner. Tente de novo.')
      .finally(() => this.salvandoBanner = false);
  }

  removaBanner(banner: any): void {
    if (!window.confirm(`Tirar o banner "${banner.nome}" do cardápio?`)) return;
    this.bannersService.removaBanner(banner).then(() => this.banners = this.banners.filter(b => b !== banner))
      .catch((e: any) => this.erroBanner = typeof e === 'string' ? e : 'Não foi possível remover o banner.');
  }

  private carregueLoja(): void {
    const loja = this.estado?.loja;
    if (!loja) return;
    const configurada = loja.delivery || loja.retirada;
    // sem nada configurado, parte do que ele contou para a Mia ("delivery e retirada", "só retirada"...)
    const contou = String(this.estado?.mia?.operacao || '').toLowerCase();
    const falouEntrega = /deliver|entreg|motoboy/.test(contou), falouRetirada = /retir|balc|buscar/.test(contou);
    this.delivery = configurada ? loja.delivery : (falouEntrega || !falouRetirada);
    this.retirada = configurada ? loja.retirada : (falouRetirada || !falouEntrega);
    this.operacaoDaMia = !configurada && (falouEntrega || falouRetirada);
    this.enderecoSalvo = loja.endereco || '';
    this.manterEndereco = !!this.enderecoSalvo;
    this.cep = loja.cep || '';
    if (loja.taxa !== null && loja.taxa !== undefined) this.taxa = this.dinheiro(loja.taxa);
    if (loja.tempoMinimo) this.tempoMinimo = String(loja.tempoMinimo);
    if (loja.tempoMaximo) this.tempoMaximo = String(loja.tempoMaximo);
    if (loja.pedidoMinimo) this.pedidoMinimo = this.dinheiro(loja.pedidoMinimo);
    if (loja.tempoRetiradaMinimo) this.tempoRetiradaMinimo = String(loja.tempoRetiradaMinimo);
    if (loja.tempoRetiradaMaximo) this.tempoRetiradaMaximo = String(loja.tempoRetiradaMaximo);
  }

  private dinheiro(valor: number): string { return Number(valor).toFixed(2).replace('.', ','); }

  private numero_(texto: string): number | null {
    const limpo = String(texto || '').replace(/[^\d.,]/g, '');
    if (!limpo) return null;
    const n = Number(limpo.includes(',') ? limpo.replace(/\./g, '').replace(',', '.') : limpo);
    return Number.isFinite(n) ? n : null;
  }

  troqueEndereco(): void {
    this.manterEndereco = false;
  }

  aoDigitarCep(): void {
    const digitos = this.cep.replace(/\D/g, '');
    this.avisoCep = '';
    if (digitos.length !== 8) return;
    this.buscandoCep = true;
    this.onboarding.busqueCep(digitos).then(e => {
      this.rua = e.rua || this.rua;
      this.bairro = e.bairro || this.bairro;
      this.cidade = e.cidade || this.cidade;
      this.uf = e.estado || this.uf;
      if (!e.rua) this.avisoCep = 'Achamos a cidade. Complete a rua.';
    }).catch(err => this.avisoCep = OnboardingPainelService.mensagem(err, 'Não achamos esse CEP. Preencha à mão.'))
      .finally(() => this.buscandoCep = false);
  }

  private tempoInvalido(min: string, max: string): boolean {
    const a = this.numero_(min), b = this.numero_(max);
    return !a || !b || a < 1 || b < a;
  }

  /** O primeiro campo que falta, com a frase que aparece no balão do guia de foco. */
  private get pendencia(): {alvo: string; mensagem: string} | null {
    if (!this.delivery && !this.retirada) return {alvo: 'onb-loja-modos', mensagem: 'Escolha como o cliente recebe: entrega, retirada ou os dois.'};
    if (!this.manterEndereco) {
      if (!this.cep.trim() && !this.rua.trim()) return {alvo: 'onb-cep', mensagem: 'Digite o CEP da loja: a gente completa o endereço.'};
      if (!this.rua.trim()) return {alvo: 'onb-rua', mensagem: 'Falta a rua da loja.'};
      if (!this.numero.trim()) return {alvo: 'onb-numero', mensagem: 'Falta o número da loja (ou escreva s/n).'};
    }
    if (this.delivery) {
      if (this.numero_(this.taxa) === null) return {alvo: 'onb-taxa', mensagem: 'Quanto você cobra de entrega? Pode ser 0 para entrega grátis.'};
      if (this.tempoInvalido(this.tempoMinimo, this.tempoMaximo)) return {alvo: 'onb-tmin', mensagem: 'Em quanto tempo você entrega? De quantos a quantos minutos.'};
    }
    if (this.retirada && this.tempoInvalido(this.tempoRetiradaMinimo, this.tempoRetiradaMaximo)) {
      return {alvo: 'onb-rmin', mensagem: 'Em quanto tempo o pedido fica pronto para retirar? De quantos a quantos minutos.'};
    }
    return null;
  }

  get dica(): string { return this.pendencia ? '' : 'Tudo certo para salvar'; }

  /** O que o cliente lê no topo da loja. */
  get previa(): string {
    const partes: string[] = [];
    if (this.delivery) {
      const taxa = this.numero_(this.taxa);
      const textoTaxa = taxa === null ? '(falta a taxa)' : taxa > 0 ? 'R$ ' + this.dinheiro(taxa) : 'grátis';
      partes.push(`Entrega ${textoTaxa} · ${this.tempoMinimo || '?'}–${this.tempoMaximo || '?'} min`);
      const minimo = this.numero_(this.pedidoMinimo);
      if (minimo) partes.push(`pedido mínimo R$ ${this.dinheiro(minimo)}`);
    }
    if (this.retirada) partes.push(`Retirada em ${this.tempoRetiradaMinimo || '?'}–${this.tempoRetiradaMaximo || '?'} min`);
    return partes.join(' · ');
  }

  salve(): void {
    if (this.salvando) return;
    const pendencia = this.pendencia;
    if (pendencia) return this.foco.mostre(pendencia.alvo, pendencia.mensagem);
    this.salvando = true;
    this.erro = '';
    this.onboarding.salveLoja({
      delivery: this.delivery, retirada: this.retirada, manterEndereco: this.manterEndereco,
      cep: this.cep, rua: this.rua, numero: this.numero, complemento: this.complemento, bairro: this.bairro,
      cidade: this.cidade, estado: this.uf,
      taxa: this.numero_(this.taxa), tempoMinimo: this.numero_(this.tempoMinimo), tempoMaximo: this.numero_(this.tempoMaximo),
      pedidoMinimo: this.numero_(this.pedidoMinimo),
      tempoRetiradaMinimo: this.numero_(this.tempoRetiradaMinimo), tempoRetiradaMaximo: this.numero_(this.tempoRetiradaMaximo)
    }).then(estado => this.concluido.emit(estado))
      .catch(e => this.erro = OnboardingPainelService.mensagem(e))
      .finally(() => this.salvando = false);
  }
}
